<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <!-- Header -->
      <div class="d-flex align-items-center justify-content-between mb-4 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-ticket-confirmation me-2"></i>{{ t('queue.title','Queue & Tickets') }}</h4>
        </div>
        <div class="d-flex gap-2">
          <button class="hms-btn hms-btn-blue" @click="openIssueTicket">
            <i class="mdi mdi-ticket-plus me-1"></i>{{ t('queue.issue_ticket','Issue Ticket') }}
          </button>
          <a v-if="displayConfig.display_token" :href="displayUrl" target="_blank" class="hms-btn hms-btn-primary">
            <i class="mdi mdi-monitor-screenshot me-1"></i>Open Display
          </a>
          <button v-else class="hms-btn hms-btn-amber" @click="tab='settings'">
            <i class="mdi mdi-cog-outline me-1"></i>Configure Display
          </button>
        </div>
      </div>

      <!-- Stats -->
      <div class="hms-stat-row">
        <div class="hms-stat" v-for="d in departments" :key="d.queue_dept_id" @click="filterDept = d.queue_dept_id; tab='live'" :class="{ selected: tab==='live' && filterDept == d.queue_dept_id }">
          <div class="hms-stat-num" :style="'color:' + d.color">{{ statsByDept[d.queue_dept_id] ? statsByDept[d.queue_dept_id].waiting : 0 }}</div>
          <div class="hms-stat-lbl"><i :class="d.icon" class="me-1"></i>{{ d.dept_name }}</div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="hms-tabs mb-3">
        <button class="hms-tab" :class="{ active: tab === 'live' }" @click="tab = 'live'">
          <i class="mdi mdi-television-play me-1"></i>Live Queue
          <span class="hms-tab-count">{{ totalWaiting }}</span>
        </button>
        <button class="hms-tab" :class="{ active: tab === 'departments' }" @click="tab = 'departments'">
          <i class="mdi mdi-domain me-1"></i>Departments
        </button>
        <button class="hms-tab" :class="{ active: tab === 'counters' }" @click="tab = 'counters'">
          <i class="mdi mdi-storefront-outline me-1"></i>Counters
        </button>
        <button class="hms-tab" :class="{ active: tab === 'today' }" @click="tab = 'today'">
          <i class="mdi mdi-history me-1"></i>Today's Tickets
        </button>
        <button class="hms-tab" :class="{ active: tab === 'settings' }" @click="tab = 'settings'">
          <i class="mdi mdi-cog-outline me-1"></i>Display Config
        </button>
      </div>

      <div v-if="loading" class="hms-spinner"><div class="spinner-border"></div></div>

      <!-- ============ LIVE QUEUE ============ -->
      <div v-else-if="tab === 'live'">
        <div class="hms-card">
          <div class="d-flex flex-wrap gap-2 mb-3 align-items-center">
            <select class="hms-input hms-filter" v-model="filterDept">
              <option value="">All departments</option>
              <option v-for="d in departments" :key="d.queue_dept_id" :value="d.queue_dept_id">{{ d.dept_name }}</option>
            </select>
            <button class="hms-btn hms-btn-blue" @click="loadWaiting"><i class="mdi mdi-refresh me-1"></i>Refresh</button>
            <span class="hms-muted ms-auto">{{ filteredWaiting.length }} waiting / called</span>
          </div>

          <div v-if="filteredWaiting.length" style="overflow-x:auto;">
            <table class="hms-table">
              <thead><tr>
                <th>Ticket</th>
                <th>Department</th>
                <th>Patient</th>
                <th>Priority</th>
                <th>Issued</th>
                <th>Status</th>
                <th>Actions</th>
              </tr></thead>
              <tbody>
                <tr v-for="t in filteredWaiting" :key="t.ticket_id" :class="t.status === 'called' ? 'hms-row-called' : ''">
                  <td><span class="hms-ticket-num" :style="'background:' + (t.color || '#1a7fe8')">{{ t.ticket_number }}</span></td>
                  <td>{{ t.dept_name }}</td>
                  <td>
                    <div class="hms-text fw-semibold">
                      {{ t.patient_name || 'Walk-in' }}
                      <span v-if="Number(t.unpaid_balance) > 0" class="hms-badge hms-badge-warning ms-1" :title="'Outstanding balance KES ' + Number(t.unpaid_balance).toLocaleString()">
                        <i class="mdi mdi-cash-remove"></i> UNPAID
                      </span>
                    </div>
                    <div class="hms-muted-sm">{{ t.phone || '' }}</div>
                  </td>
                  <td><span class="hms-badge" :class="priorityBadge(t.priority)">{{ formatPriority(t.priority) }}</span></td>
                  <td>{{ formatTime(t.issued_at) }}</td>
                  <td>
                    <span class="hms-badge" :class="ticketStatusBadge(t.status)">{{ formatTicketStatus(t.status) }}</span>
                    <span v-if="t.recall_count > 0" class="hms-muted-sm ms-1">(R{{ t.recall_count }})</span>
                  </td>
                  <td class="d-flex gap-1">
                    <button v-if="t.status === 'waiting'" class="hms-btn hms-btn-sm hms-btn-primary" @click="quickCallNext(t)" title="Call next">
                      <i class="mdi mdi-bullhorn me-1"></i>Call
                    </button>
                    <button v-if="t.status === 'called'" class="hms-btn hms-btn-sm hms-btn-blue" @click="recall(t)" title="Recall">
                      <i class="mdi mdi-bullhorn-variant"></i>
                    </button>
                    <button v-if="t.status === 'called'" class="hms-btn hms-btn-sm hms-btn-success" @click="serve(t)" title="Mark served">
                      <i class="mdi mdi-check"></i>
                    </button>
                    <button class="hms-btn hms-btn-sm hms-btn-amber" @click="openTransfer(t)" title="Transfer">
                      <i class="mdi mdi-transit-transfer"></i>
                    </button>
                    <button class="hms-btn hms-btn-sm hms-btn-red" @click="cancel(t)" title="Cancel">
                      <i class="mdi mdi-close"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="hms-empty">
            <i class="mdi mdi-ticket-outline"></i>
            No waiting tickets. Issue one with the button at the top.
          </div>
        </div>
      </div>

      <!-- ============ DEPARTMENTS ============ -->
      <div v-else-if="tab === 'departments'">
        <div class="hms-card">
          <div class="d-flex justify-content-between mb-3">
            <span class="hms-muted">{{ departments.length }} departments</span>
            <button class="hms-btn hms-btn-primary hms-btn-sm" @click="openAddDept">
              <i class="mdi mdi-plus me-1"></i>Add Department
            </button>
          </div>
          <div style="overflow-x:auto;" v-if="departments.length">
            <table class="hms-table">
              <thead><tr><th>Name</th><th>Code</th><th>Prefix</th><th>Avg Serve</th><th>Order</th><th>Active</th><th></th></tr></thead>
              <tbody>
                <tr v-for="d in departments" :key="d.queue_dept_id">
                  <td><i :class="d.icon" :style="'color:' + d.color" class="me-2"></i><span class="hms-text fw-semibold">{{ d.dept_name }}</span></td>
                  <td>{{ d.dept_code || '—' }}</td>
                  <td><span class="hms-mono">{{ d.ticket_prefix }}</span></td>
                  <td>{{ d.avg_serve_mins }} min</td>
                  <td>{{ d.display_order }}</td>
                  <td><span class="hms-badge" :class="d.is_active == 1 ? 'hms-badge-success' : 'hms-badge-neutral'">{{ d.is_active == 1 ? 'Active' : 'Inactive' }}</span></td>
                  <td>
                    <button class="hms-btn hms-btn-sm hms-btn-amber" @click="openEditDept(d)"><i class="mdi mdi-pencil"></i></button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="hms-empty"><i class="mdi mdi-domain"></i>No departments yet.</div>
        </div>
      </div>

      <!-- ============ COUNTERS ============ -->
      <div v-else-if="tab === 'counters'">
        <div class="hms-card">
          <div class="d-flex justify-content-between mb-3">
            <span class="hms-muted">{{ counters.length }} counters</span>
            <button class="hms-btn hms-btn-primary hms-btn-sm" @click="openAddCounter">
              <i class="mdi mdi-plus me-1"></i>Add Counter
            </button>
          </div>
          <div style="overflow-x:auto;" v-if="counters.length">
            <table class="hms-table">
              <thead><tr><th>Counter</th><th>Department</th><th>Assigned</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                <tr v-for="c in counters" :key="c.counter_id">
                  <td>
                    <div class="hms-text fw-semibold">{{ c.counter_name }}</div>
                    <div class="hms-muted-sm">{{ c.counter_code || '' }}</div>
                  </td>
                  <td>{{ c.dept_name }}</td>
                  <td>{{ c.assigned_user_name || '—' }}</td>
                  <td><span class="hms-badge" :class="counterBadge(c.status)">{{ c.status }}</span></td>
                  <td class="d-flex gap-1">
                    <button v-if="c.status !== 'open'" class="hms-btn hms-btn-sm hms-btn-success" @click="openCounter(c)"><i class="mdi mdi-play me-1"></i>Open</button>
                    <button v-if="c.status === 'open'" class="hms-btn hms-btn-sm hms-btn-amber" @click="pauseCounter(c)"><i class="mdi mdi-pause me-1"></i>Pause</button>
                    <button v-if="c.status !== 'closed'" class="hms-btn hms-btn-sm hms-btn-red" @click="closeCounter(c)"><i class="mdi mdi-stop me-1"></i>Close</button>
                    <button v-if="c.status === 'open'" class="hms-btn hms-btn-sm hms-btn-primary" @click="callNextForCounter(c)"><i class="mdi mdi-bullhorn me-1"></i>Call Next</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="hms-empty"><i class="mdi mdi-storefront-outline"></i>No counters yet.</div>
        </div>
      </div>

      <!-- ============ TODAY'S TICKETS ============ -->
      <div v-else-if="tab === 'today'">
        <div class="hms-card">
          <div class="d-flex flex-wrap gap-2 mb-3 align-items-center">
            <input type="date" class="hms-input" style="max-width:180px;" v-model="ticketsDate" @change="loadToday" />
            <span class="hms-muted">{{ todayTickets.length }} tickets</span>
          </div>
          <div style="overflow-x:auto;" v-if="todayTickets.length">
            <table class="hms-table">
              <thead><tr>
                <th>Ticket</th><th>Dept</th><th>Patient</th><th>Priority</th>
                <th>Issued</th><th>Wait</th><th>Serve</th><th>Status</th>
              </tr></thead>
              <tbody>
                <tr v-for="t in todayTickets" :key="t.ticket_id">
                  <td><span class="hms-ticket-num" :style="'background:' + (t.color || '#1a7fe8')">{{ t.ticket_number }}</span></td>
                  <td>{{ t.dept_name }}</td>
                  <td>
                    {{ t.patient_full_name || t.patient_name || 'Walk-in' }}<span v-if="t.patient_no" class="hms-muted-sm"> · {{ t.patient_no }}</span>
                    <span v-if="Number(t.unpaid_balance) > 0" class="hms-badge hms-badge-warning ms-1" :title="'Outstanding balance KES ' + Number(t.unpaid_balance).toLocaleString()">
                      <i class="mdi mdi-cash-remove"></i> UNPAID
                    </span>
                  </td>
                  <td><span class="hms-badge" :class="priorityBadge(t.priority)">{{ formatPriority(t.priority) }}</span></td>
                  <td>{{ formatTime(t.issued_at) }}</td>
                  <td>{{ t.wait_minutes != null ? t.wait_minutes + 'm' : '—' }}</td>
                  <td>{{ t.serve_minutes != null ? t.serve_minutes + 'm' : '—' }}</td>
                  <td><span class="hms-badge" :class="ticketStatusBadge(t.status)">{{ formatTicketStatus(t.status) }}</span></td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="hms-empty"><i class="mdi mdi-history"></i>No tickets today.</div>
        </div>
      </div>

      <!-- ============ DISPLAY CONFIG ============ -->
      <div v-else-if="tab === 'settings'">
        <div class="hms-card">
          <h6 class="hms-section-label mb-2">PUBLIC DISPLAY URL</h6>
          <div v-if="displayConfig.display_token" class="hms-display-url-box mb-3">
            <i class="mdi mdi-link-variant me-2"></i>
            <code>{{ displayUrl }}</code>
            <button class="hms-btn hms-btn-sm hms-btn-blue ms-auto" @click="copyDisplayUrl"><i class="mdi mdi-content-copy me-1"></i>Copy</button>
          </div>
          <div v-else class="hms-muted-sm mb-3">No display configured yet — save below to generate a public URL.</div>

          <div v-if="displayError" class="hms-alert hms-alert-danger">{{ displayError }}</div>

          <div class="row g-3">
            <div class="col-md-6"><label class="hms-label">Display Name</label>
              <input class="hms-input" v-model="displayConfig.display_name" placeholder="Main Lobby Display" />
            </div>
            <div class="col-md-3"><label class="hms-label">Now Serving Count</label>
              <input type="number" class="hms-input" v-model.number="displayConfig.now_serving_count" min="1" max="10" />
            </div>
            <div class="col-md-3"><label class="hms-label">Next Up Count</label>
              <input type="number" class="hms-input" v-model.number="displayConfig.next_up_count" min="1" max="20" />
            </div>
            <div class="col-md-12"><label class="hms-label">Departments to show <span class="hms-muted-sm">(comma-separated IDs, blank = all)</span></label>
              <input class="hms-input" v-model="displayConfig.show_dept_ids" placeholder="1,2,3,4,5" />
            </div>
            <div class="col-md-4"><label class="hms-label">Theme</label>
              <select class="hms-input" v-model="displayConfig.theme">
                <option value="light">Light</option>
                <option value="dark">Dark</option>
              </select>
            </div>
            <div class="col-md-4"><label class="hms-label">Voice / Language</label>
              <select class="hms-input" v-model="displayConfig.announcement_voice">
                <option value="en-US">English (US)</option>
                <option value="en-GB">English (UK)</option>
                <option value="sw-KE">Swahili (Kenya)</option>
              </select>
            </div>
            <div class="col-md-4"><label class="hms-label">Volume</label>
              <input type="number" class="hms-input" v-model.number="displayConfig.announcement_volume" min="0" max="1" step="0.1" />
            </div>
            <div class="col-md-12"><label class="hms-label">Scroll Message (marquee)</label>
              <textarea class="hms-input" rows="2" v-model="displayConfig.scroll_message" placeholder="Welcome message that scrolls along the bottom of the display"></textarea>
            </div>
            <div class="col-12 d-flex flex-wrap gap-3">
              <label class="hms-check"><input type="checkbox" v-model="displayConfig.show_logo" :true-value="1" :false-value="0" /> Show logo</label>
              <label class="hms-check"><input type="checkbox" v-model="displayConfig.show_clock" :true-value="1" :false-value="0" /> Show clock</label>
            </div>
          </div>

          <button class="hms-btn hms-btn-primary mt-3" @click="saveDisplayConfig" :disabled="processing">
            <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="mdi mdi-content-save me-1"></i>{{ processing ? 'Saving...' : 'Save Display Config' }}
          </button>
        </div>
      </div>

      <!-- ============ ISSUE TICKET MODAL ============ -->
      <div class="hms-modal-overlay" v-if="showIssue" @click.self="showIssue=false">
        <div class="hms-modal" style="max-width:560px;">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-ticket-plus me-2"></i>Issue Ticket</span>
            <button class="hms-modal-close" @click="showIssue=false">&times;</button>
          </div>
          <div v-if="issueError" class="hms-alert hms-alert-danger">{{ issueError }}</div>

          <div class="row g-3 mb-3">
            <div class="col-md-7"><label class="hms-label">Department *</label>
              <select class="hms-input" v-model="issueForm.queue_dept_id">
                <option value="">Select...</option>
                <option v-for="d in departments" :key="d.queue_dept_id" :value="d.queue_dept_id">{{ d.dept_name }} ({{ d.ticket_prefix }})</option>
              </select>
            </div>
            <div class="col-md-5"><label class="hms-label">Priority</label>
              <select class="hms-input" v-model="issueForm.priority">
                <option value="emergency">Emergency</option>
                <option value="elderly_disabled">Elderly / Disabled</option>
                <option value="urgent">Urgent</option>
                <option value="normal">Normal</option>
              </select>
            </div>

            <!-- ============ PATIENT PICKER (primary) ============ -->
            <div class="col-12" v-if="!issueForm.patient_id && !walkInMode">
              <label class="hms-label">Patient — search by name, patient no, phone or ID *</label>
              <div class="hms-search-wrap">
                <i class="mdi mdi-magnify"></i>
                <input class="hms-search" v-model="patientSearch" placeholder="Start typing — e.g. Alex, PAT110…, 0704…, 32699686" @input="onPatientSearch" autofocus />
              </div>
              <div v-if="patientResults.length" class="hms-suggest mt-1">
                <div v-for="pp in patientResults" :key="pp.patient_id" class="hms-suggest-item" @click="pickPatient(pp)">
                  <div class="d-flex justify-content-between align-items-start">
                    <div>
                      <div class="hms-text fw-semibold">{{ pp.first_name }} {{ pp.last_name }} <span v-if="pp.other_names" class="hms-muted-sm">{{ pp.other_names }}</span></div>
                      <div class="hms-muted-sm">
                        <span class="hms-mono">{{ pp.patient_no }}</span>
                        <span v-if="pp.phone"> · {{ pp.phone }}</span>
                        <span v-if="pp.national_id"> · ID {{ pp.national_id }}</span>
                        <span v-if="pp.dob"> · {{ ageFromDob(pp.dob) }} yrs</span>
                      </div>
                    </div>
                    <i class="mdi mdi-arrow-right-circle hms-text-primary" style="font-size:18px;"></i>
                  </div>
                </div>
              </div>
              <div v-else-if="patientSearch.length >= 2 && !patientSearchTimer" class="hms-muted-sm mt-2">
                No matches. <button class="hms-btn-link" @click="enterWalkInMode">Issue to walk-in instead →</button>
              </div>
              <div v-else class="hms-muted-sm mt-2">
                <button class="hms-btn-link" @click="enterWalkInMode">Or issue to walk-in (not registered) →</button>
              </div>
            </div>

            <!-- ============ PICKED PATIENT CARD ============ -->
            <div class="col-12" v-if="issueForm.patient_id">
              <label class="hms-label">Patient</label>
              <div class="hms-picked-card">
                <div class="hms-picked-avatar"><i class="mdi mdi-account-circle"></i></div>
                <div class="hms-picked-info">
                  <div class="hms-text fw-semibold">{{ issueForm.patient_name }}</div>
                  <div class="hms-muted-sm">
                    <span class="hms-mono">{{ issueForm.patient_no }}</span>
                    <span v-if="issueForm.phone"> · {{ issueForm.phone }}</span>
                  </div>
                </div>
                <button class="hms-btn hms-btn-sm hms-btn-red" @click="clearPatient" title="Change patient">
                  <i class="mdi mdi-close"></i>
                </button>
              </div>
            </div>

            <!-- ============ WALK-IN FALLBACK ============ -->
            <template v-if="walkInMode && !issueForm.patient_id">
              <div class="col-12">
                <div class="hms-alert hms-alert-amber" style="padding:8px 12px;">
                  <i class="mdi mdi-walk me-1"></i>
                  <strong>Walk-in mode</strong> — patient is not in the system. Notifications won't be linked to a patient record.
                  <button class="hms-btn-link ms-2" @click="exitWalkInMode">← Search registered patient instead</button>
                </div>
              </div>
              <div class="col-md-7"><label class="hms-label">Patient Name *</label>
                <input class="hms-input" v-model="issueForm.patient_name" placeholder="Full name" autofocus />
              </div>
              <div class="col-md-5"><label class="hms-label">Phone</label>
                <input class="hms-input" v-model="issueForm.phone" placeholder="0712345678" />
                <div class="hms-muted-sm mt-1">Used for transfer SMS notifications.</div>
              </div>
            </template>

            <div class="col-12"><label class="hms-label">Notes</label>
              <textarea class="hms-input" rows="2" v-model="issueForm.notes" placeholder="Optional"></textarea>
            </div>
          </div>

          <button class="hms-btn hms-btn-primary w-100" @click="doIssueTicket" :disabled="processing || !issueForm.queue_dept_id">
            <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
            {{ processing ? 'Issuing...' : 'Issue Ticket' }}
          </button>

          <div v-if="lastTicket" class="hms-ticket-receipt mt-3">
            <div class="hms-receipt-label">Ticket issued:</div>
            <div class="hms-receipt-number">{{ lastTicket.ticket_number }}</div>
            <div class="hms-receipt-meta">{{ lastTicket.dept_name }} · {{ lastTicket.priority }} · ETA {{ lastTicket.estimated_wait }}</div>
          </div>
        </div>
      </div>

      <!-- ============ ADD/EDIT DEPARTMENT MODAL ============ -->
      <div class="hms-modal-overlay" v-if="showDeptForm" @click.self="showDeptForm=false">
        <div class="hms-modal" style="max-width:560px;">
          <div class="hms-modal-title">
            <span><i class="mdi" :class="editingDept ? 'mdi-pencil' : 'mdi-plus-circle'"></i> {{ editingDept ? 'Edit Department' : 'Add Department' }}</span>
            <button class="hms-modal-close" @click="showDeptForm=false">&times;</button>
          </div>
          <div v-if="deptError" class="hms-alert hms-alert-danger">{{ deptError }}</div>

          <div class="row g-3 mb-3">
            <div class="col-md-7"><label class="hms-label">Department Name *</label>
              <input class="hms-input" v-model="deptForm.dept_name" placeholder="OPD Reception" />
            </div>
            <div class="col-md-5"><label class="hms-label">Code</label>
              <input class="hms-input" v-model="deptForm.dept_code" placeholder="OPD" />
            </div>
            <div class="col-md-4"><label class="hms-label">Ticket Prefix *</label>
              <input class="hms-input" v-model="deptForm.ticket_prefix" placeholder="OPD" maxlength="10" />
            </div>
            <div class="col-md-4"><label class="hms-label">Color</label>
              <input type="color" class="hms-input" v-model="deptForm.color" />
            </div>
            <div class="col-md-4"><label class="hms-label">Icon (mdi class)</label>
              <input class="hms-input" v-model="deptForm.icon" placeholder="mdi mdi-account-clock" />
            </div>
            <div class="col-md-4"><label class="hms-label">Avg Serve (min)</label>
              <input type="number" class="hms-input" v-model.number="deptForm.avg_serve_mins" min="1" />
            </div>
            <div class="col-md-4"><label class="hms-label">Display Order</label>
              <input type="number" class="hms-input" v-model.number="deptForm.display_order" min="0" />
            </div>
            <div class="col-md-4 d-flex align-items-end">
              <label class="hms-check"><input type="checkbox" v-model="deptForm.is_active" :true-value="1" :false-value="0" /> Active</label>
            </div>
          </div>

          <button class="hms-btn hms-btn-primary w-100" @click="saveDept" :disabled="processing">
            <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
            {{ processing ? 'Saving...' : (editingDept ? 'Save Changes' : 'Create Department') }}
          </button>
        </div>
      </div>

      <!-- ============ ADD COUNTER MODAL ============ -->
      <div class="hms-modal-overlay" v-if="showCounterForm" @click.self="showCounterForm=false">
        <div class="hms-modal" style="max-width:480px;">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-plus-circle me-2"></i>Add Counter</span>
            <button class="hms-modal-close" @click="showCounterForm=false">&times;</button>
          </div>
          <div v-if="counterError" class="hms-alert hms-alert-danger">{{ counterError }}</div>

          <div class="mb-3"><label class="hms-label">Department *</label>
            <select class="hms-input" v-model="counterForm.queue_dept_id">
              <option value="">Select...</option>
              <option v-for="d in departments" :key="d.queue_dept_id" :value="d.queue_dept_id">{{ d.dept_name }}</option>
            </select>
          </div>
          <div class="row g-3 mb-3">
            <div class="col-7"><label class="hms-label">Counter Name *</label>
              <input class="hms-input" v-model="counterForm.counter_name" placeholder="OPD Counter 1" />
            </div>
            <div class="col-5"><label class="hms-label">Code</label>
              <input class="hms-input" v-model="counterForm.counter_code" placeholder="OPD-1" />
            </div>
          </div>

          <button class="hms-btn hms-btn-primary w-100" @click="saveCounter" :disabled="processing">
            <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
            {{ processing ? 'Saving...' : 'Create Counter' }}
          </button>
        </div>
      </div>

      <!-- ============ TRANSFER MODAL ============ -->
      <div class="hms-modal-overlay" v-if="showTransfer" @click.self="showTransfer=false">
        <div class="hms-modal" style="max-width:480px;" v-if="transferTicket">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-transit-transfer me-2"></i>Transfer Ticket</span>
            <button class="hms-modal-close" @click="showTransfer=false">&times;</button>
          </div>
          <div class="hms-kv mb-3">
            <div><span class="hms-kv-key">Ticket</span><span class="hms-mono">{{ transferTicket.ticket_number }}</span></div>
            <div><span class="hms-kv-key">From</span><span>{{ transferTicket.dept_name }}</span></div>
          </div>
          <div class="mb-3"><label class="hms-label">Destination Department *</label>
            <select class="hms-input" v-model="transferToDept">
              <option value="">Select...</option>
              <option v-for="d in departments.filter(x => x.queue_dept_id != transferTicket.queue_dept_id)" :key="d.queue_dept_id" :value="d.queue_dept_id">{{ d.dept_name }}</option>
            </select>
          </div>
          <button class="hms-btn hms-btn-primary w-100" @click="doTransfer" :disabled="processing || !transferToDept">
            <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>Transfer
          </button>
        </div>
      </div>

      <!-- Toast -->
      <transition name="hms-toast">
        <div v-if="toastMsg" class="hms-toast" :class="{ 'hms-toast-error': toastError }">
          <i class="mdi me-2" :class="toastError ? 'mdi-alert-circle' : 'mdi-check-circle'"></i>{{ toastMsg }}
        </div>
      </transition>
    </div>
  </div>
</template>

<style>
:root,
html[data-theme="dark"] {
  --hms-h:#f0f0f0;--hms-s:#a0a0a0;--hms-m:#808080;
  --hms-bg1:#141414;--hms-bg2:#0a0a0a;--hms-bg3:#1e1e1e;--hms-bg4:#0f0f0f;
  --hms-br:#2a2a2a;--hms-input-br:#333333;--hms-placeholder:#4a4a4a;
  --hms-primary:#1a7fe8;--hms-primary-lt:rgba(26,127,232,0.12);
  --hms-accent:#F5821F;--hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
}
html[data-theme="light"] {
  --hms-h:#1a1a1a;--hms-s:#555555;--hms-m:#777777;
  --hms-bg1:#ffffff;--hms-bg2:#f4f6f9;--hms-bg3:#eef1f5;--hms-bg4:#ffffff;
  --hms-br:#dee2e8;--hms-input-br:#ced4da;--hms-placeholder:#aaaaaa;
  --hms-primary:#0047AB;--hms-primary-lt:rgba(0,71,171,0.08);
  --hms-accent:#F5821F;--hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
}
html[data-theme="light"] body, html[data-theme="dark"] body { background: var(--hms-bg2) !important; color: var(--hms-h) !important; }
</style>

<style scoped>
.hms-page { min-height: 100vh; background: var(--hms-bg2); padding-top: 70px; color: var(--hms-h); }
.hms-page-body { padding-top: 20px; padding-bottom: 40px; }
.hms-page-header { flex-wrap: wrap; gap: 12px; }
.hms-back { color: var(--hms-s); font-size: 22px; text-decoration: none; padding: 4px; }
.hms-back:hover { color: var(--hms-primary); }
.hms-page-title { color: var(--hms-h); font-size: 20px; font-weight: 700; }

.hms-muted { color: var(--hms-s); font-size: 13px; }
.hms-muted-sm { color: var(--hms-s); font-size: 12px; }
.hms-text { color: var(--hms-h); }
.hms-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 13px; }

.hms-stat-row { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 20px; }
.hms-stat { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 12px; padding: 14px 18px; flex: 1; min-width: 150px; cursor: pointer; transition: all .15s; }
.hms-stat:hover { border-color: var(--hms-primary); transform: translateY(-2px); }
.hms-stat.selected { border-color: var(--hms-primary); background: var(--hms-primary-lt); }
.hms-stat-num { font-size: 22px; font-weight: 800; color: var(--hms-primary); line-height: 1.1; }
.hms-stat-lbl { font-size: 12px; color: var(--hms-s); margin-top: 4px; }

.hms-tabs { display: flex; flex-wrap: wrap; gap: 6px; }
.hms-tab { background: var(--hms-bg1); border: 1px solid var(--hms-br); color: var(--hms-m); border-radius: 50px; padding: 7px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; }
.hms-tab:hover, .hms-tab.active { background: var(--hms-primary-lt); border-color: var(--hms-primary); color: var(--hms-primary); }
.hms-tab-count { background: var(--hms-primary); color: #fff; border-radius: 999px; padding: 1px 7px; font-size: 10px; margin-left: 8px; }

.hms-card { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 14px; padding: 20px; margin-bottom: 16px; }
.hms-spinner { display: flex; justify-content: center; padding: 40px; }
.hms-spinner .spinner-border { color: var(--hms-primary); }
.hms-empty { text-align: center; padding: 40px 20px; color: var(--hms-s); }
.hms-empty i { font-size: 48px; color: var(--hms-br); display: block; margin-bottom: 12px; }

.hms-search-wrap { position: relative; flex: 1; min-width: 240px; }
.hms-search-wrap i { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--hms-s); }
.hms-search { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px 8px 36px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-search:focus { border-color: var(--hms-primary); }

.hms-input { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-input:focus { border-color: var(--hms-primary); }
.hms-filter { width: auto; padding: 7px 12px; font-size: 13px; }
.hms-label { color: var(--hms-m); font-size: 12px; font-weight: 600; margin-bottom: 4px; display: block; }
.hms-section-label { font-size: 11px; font-weight: 700; color: var(--hms-s); letter-spacing: 1.5px; }
.hms-check { display: inline-flex; align-items: center; gap: 6px; color: var(--hms-h); font-size: 13px; cursor: pointer; }
.hms-check input { accent-color: var(--hms-primary); }

.hms-table { width: 100%; border-collapse: collapse; }
.hms-table th { color: var(--hms-s); font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; padding: 10px 12px; border-bottom: 1px solid var(--hms-br); text-align: left; white-space: nowrap; }
.hms-table td { color: var(--hms-h); font-size: 13px; padding: 10px 12px; border-bottom: 1px solid var(--hms-br); vertical-align: middle; }
.hms-table tr:hover td { background: var(--hms-primary-lt); }
.hms-row-called td { background: rgba(40,167,69,.08); }

.hms-ticket-num { display: inline-block; padding: 4px 10px; border-radius: 6px; color: #fff; font-weight: 800; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 13px; min-width: 80px; text-align: center; }

.hms-badge { padding: 3px 10px; border-radius: 50px; font-size: 11px; font-weight: 600; text-transform: capitalize; display: inline-block; }
.hms-badge-success { background: rgba(40,167,69,.15); color: var(--hms-success); }
.hms-badge-warning { background: rgba(255,193,7,.15); color: var(--hms-warning); }
.hms-badge-danger  { background: rgba(220,53,69,.15);  color: var(--hms-danger); }
.hms-badge-info    { background: var(--hms-primary-lt); color: var(--hms-primary); }
.hms-badge-neutral { background: var(--hms-bg3); color: var(--hms-m); }
.hms-badge-purple  { background: rgba(155,89,182,.15); color: #9b59b6; }
.hms-badge-amber   { background: rgba(245,158,11,.15); color: #f59e0b; }

.hms-btn { border: none; border-radius: 8px; padding: 8px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; justify-content: center; }
.hms-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.hms-btn-sm { padding: 5px 10px; font-size: 12px; }
.hms-btn-primary { background: var(--hms-primary); color: #fff; }
.hms-btn-primary:hover:not(:disabled) { filter: brightness(1.1); }
.hms-btn-blue { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); }
.hms-btn-blue:hover:not(:disabled) { background: var(--hms-primary); color: #fff; }
.hms-btn-amber { background: rgba(245,158,11,.15); color: #f59e0b; border: 1px solid rgba(245,158,11,.3); }
.hms-btn-amber:hover:not(:disabled) { background: rgba(245,158,11,.25); }
.hms-btn-red    { background: rgba(220,53,69,.15);   color: var(--hms-danger); border: 1px solid rgba(220,53,69,.3); }
.hms-btn-red:hover:not(:disabled) { background: rgba(220,53,69,.25); }
.hms-btn-success { background: rgba(40,167,69,.15); color: var(--hms-success); border: 1px solid rgba(40,167,69,.3); }
.hms-btn-success:hover:not(:disabled) { background: var(--hms-success); color: #fff; }

.hms-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.6); z-index: 200; display: flex; align-items: flex-start; justify-content: center; padding: 40px 20px; overflow-y: auto; }
.hms-modal { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 16px; padding: 24px; width: 100%; max-width: 720px; max-height: calc(100vh - 80px); overflow-y: auto; box-shadow: 0 24px 60px rgba(0,0,0,.3); }
.hms-modal-title { color: var(--hms-h); font-size: 18px; font-weight: 700; margin-bottom: 18px; display: flex; align-items: center; justify-content: space-between; }
.hms-modal-close { color: var(--hms-s); cursor: pointer; font-size: 24px; line-height: 1; background: none; border: none; padding: 0 4px; }
.hms-modal-close:hover { color: var(--hms-h); }

.hms-kv { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 10px; padding: 12px 16px; }
.hms-kv > div { display: flex; padding: 6px 0; border-bottom: 1px dashed var(--hms-br); font-size: 13px; color: var(--hms-h); }
.hms-kv > div:last-child { border-bottom: none; }
.hms-kv-key { color: var(--hms-s); flex: 0 0 120px; font-weight: 600; }

.hms-suggest { background: var(--hms-bg4); border: 1px solid var(--hms-br); border-radius: 8px; margin-top: 6px; max-height: 200px; overflow-y: auto; }
.hms-suggest-item { padding: 8px 12px; cursor: pointer; border-bottom: 1px solid var(--hms-br); }
.hms-suggest-item:last-child { border-bottom: none; }
.hms-suggest-item:hover { background: var(--hms-primary-lt); }
.hms-picked { background: rgba(40,167,69,.08); border: 1px solid rgba(40,167,69,.3); border-radius: 8px; padding: 8px 12px; color: var(--hms-success); font-size: 13px; display: flex; align-items: center; }
.hms-picked-card { display: flex; align-items: center; gap: 12px; background: rgba(40,167,69,.08); border: 1px solid rgba(40,167,69,.3); border-radius: 10px; padding: 10px 14px; }
.hms-picked-avatar i { font-size: 32px; color: var(--hms-success); }
.hms-picked-info { flex: 1; min-width: 0; }
.hms-text-primary { color: var(--hms-primary); }
.hms-btn-link { background: none; border: none; color: var(--hms-primary); font-size: 12px; cursor: pointer; padding: 0; text-decoration: underline; }
.hms-btn-link:hover { color: var(--hms-h); }
.hms-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; }

.hms-display-url-box { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 10px; padding: 10px 14px; display: flex; align-items: center; gap: 8px; font-size: 13px; }
.hms-display-url-box code { color: var(--hms-primary); flex: 1; word-break: break-all; }

.hms-ticket-receipt { background: var(--hms-primary-lt); border: 2px dashed var(--hms-primary); border-radius: 12px; padding: 16px; text-align: center; }
.hms-receipt-label { color: var(--hms-s); font-size: 11px; text-transform: uppercase; letter-spacing: 1px; }
.hms-receipt-number { color: var(--hms-primary); font-weight: 800; font-size: 28px; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; line-height: 1.2; margin-top: 4px; }
.hms-receipt-meta { color: var(--hms-m); font-size: 12px; margin-top: 4px; }

.hms-alert { padding: 10px 14px; border-radius: 8px; font-size: 13px; margin-bottom: 12px; }
.hms-alert-danger { background: rgba(220,53,69,.1); color: var(--hms-danger); border: 1px solid rgba(220,53,69,.3); }
.hms-alert-amber  { background: rgba(245,158,11,.10); color: #b45309; border: 1px solid rgba(245,158,11,.35); }

.hms-toast { position: fixed; bottom: 24px; right: 24px; background: var(--hms-bg1); border: 1px solid var(--hms-success); color: var(--hms-h); padding: 12px 18px; border-radius: 10px; box-shadow: 0 12px 32px rgba(40,167,69,.2); z-index: 1000; font-size: 13px; display: flex; align-items: center; }
.hms-toast i { color: var(--hms-success); }
.hms-toast.hms-toast-error { border-color: var(--hms-danger); box-shadow: 0 12px 32px rgba(220,53,69,.2); }
.hms-toast.hms-toast-error i { color: var(--hms-danger); }
.hms-toast-enter-active, .hms-toast-leave-active { transition: all .25s ease; }
.hms-toast-enter-from, .hms-toast-leave-to { opacity: 0; transform: translateY(20px); }
</style>

<script>
/* eslint-disable */
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";
import { useI18n } from "@/api/i18n";

export default {
  name: "QueueManagement",
  components: { Navigation },
  data() {
    return {
      loading: true, processing: false,
      tab: "live",
      filterDept: "",
      i18n: useI18n(),

      departments: [],
      counters:    [],
      waiting:     [],
      todayTickets: [],
      ticketsDate: new Date().toISOString().slice(0, 10),
      displayConfig: this.emptyDisplayConfig(),
      displayError: "",

      // Issue ticket
      showIssue: false,
      walkInMode: false,
      issueError: "",
      issueForm: this.emptyIssueForm(),
      patientSearch: "",
      patientSearchTimer: null,
      patientResults: [],
      lastTicket: null,

      // Dept form
      showDeptForm: false,
      editingDept: false,
      deptError: "",
      deptForm: this.emptyDeptForm(),

      // Counter form
      showCounterForm: false,
      counterError: "",
      counterForm: { queue_dept_id: "", counter_name: "", counter_code: "" },

      // Transfer
      showTransfer: false,
      transferTicket: null,
      transferToDept: "",

      // Toast
      toastMsg: "", toastError: false, toastTimer: null,

      branchId:   sessionStorage.getItem("branchId"),
      hospitalId: sessionStorage.getItem("hospitalId")
    };
  },
  computed: {
    filteredWaiting() {
      if (!this.filterDept) return this.waiting;
      return this.waiting.filter(t => t.queue_dept_id == this.filterDept);
    },
    totalWaiting() {
      return this.waiting.filter(t => t.status === 'waiting').length;
    },
    statsByDept() {
      var map = {};
      this.departments.forEach(d => map[d.queue_dept_id] = { waiting: 0, called: 0 });
      this.waiting.forEach(t => {
        if (!map[t.queue_dept_id]) return;
        if (t.status === 'waiting') map[t.queue_dept_id].waiting++;
        else if (t.status === 'called') map[t.queue_dept_id].called++;
      });
      return map;
    },
    displayUrl() {
      if (!this.displayConfig.display_token) return "";
      return window.location.origin + window.location.pathname + "#/queue-display?token=" + this.displayConfig.display_token;
    }
  },
  mounted() {
    if (!sessionStorage.getItem("isLoggedIn")) {
      this.$router.push("/login");
      return;
    }
    this.applyTheme();
    this.loadAll();
    this.refreshTimer = setInterval(() => { if (this.tab === 'live') this.loadWaiting(); }, 10000);
  },
  beforeUnmount() {
    if (this.refreshTimer) clearInterval(this.refreshTimer);
    if (this.patientSearchTimer) clearTimeout(this.patientSearchTimer);
    if (this.toastTimer) clearTimeout(this.toastTimer);
  },
  methods: {
    t(key, fallback) {
      return (this.i18n && this.i18n.state.strings[key]) || fallback || key;
    },
    applyTheme() {
      var t = localStorage.getItem("hms_theme") || document.documentElement.getAttribute("data-theme") || "light";
      document.documentElement.setAttribute("data-theme", t);
    },
    emptyIssueForm() {
      return { queue_dept_id: "", patient_id: null, patient_no: "", patient_name: "", phone: "", priority: "normal", notes: "" };
    },
    emptyDeptForm() {
      return { queue_dept_id: null, dept_name: "", dept_code: "", ticket_prefix: "", color: "#1a7fe8", icon: "mdi mdi-account-clock", avg_serve_mins: 10, display_order: 0, is_active: 1 };
    },
    emptyDisplayConfig() {
      return { display_token: "", display_name: "Main Display", show_dept_ids: "", now_serving_count: 3, next_up_count: 5, theme: "light", show_logo: 1, show_clock: 1, announcement_voice: "en-US", announcement_volume: 1.0, scroll_message: "" };
    },

    formatTime(d) {
      if (!d) return "—";
      try { var dt = new Date(d); if (isNaN(dt.getTime())) return d; return dt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }); } catch(e) { return d; }
    },
    formatPriority(p) { return ({ emergency:'Emergency', elderly_disabled:'Elderly/Disabled', urgent:'Urgent', normal:'Normal' })[p] || p; },
    priorityBadge(p) {
      switch(p) { case 'emergency': return 'hms-badge-danger'; case 'elderly_disabled': return 'hms-badge-purple'; case 'urgent': return 'hms-badge-amber'; default: return 'hms-badge-neutral'; }
    },
    formatTicketStatus(s) { return ({ waiting:'Waiting', called:'Called', serving:'Serving', served:'Served', no_show:'No Show', skipped:'Skipped', cancelled:'Cancelled', transferred:'Transferred' })[s] || s; },
    ticketStatusBadge(s) {
      switch(s) { case 'waiting': return 'hms-badge-amber'; case 'called': return 'hms-badge-info'; case 'serving': return 'hms-badge-purple'; case 'served': return 'hms-badge-success'; case 'no_show':case 'skipped':case 'cancelled': return 'hms-badge-danger'; default: return 'hms-badge-neutral'; }
    },
    counterBadge(s) {
      switch(s) { case 'open': return 'hms-badge-success'; case 'paused': return 'hms-badge-amber'; case 'closed': return 'hms-badge-neutral'; case 'offline': return 'hms-badge-danger'; default: return 'hms-badge-neutral'; }
    },

    showToast(msg, isError) {
      this.toastMsg = msg; this.toastError = !!isError;
      if (this.toastTimer) clearTimeout(this.toastTimer);
      var self = this;
      this.toastTimer = setTimeout(() => { self.toastMsg = ""; }, 2400);
    },

    async loadAll() {
      this.loading = true;
      try { await Promise.all([this.loadDepartments(), this.loadCounters(), this.loadWaiting(), this.loadToday(), this.loadDisplayConfig()]); }
      catch(e) { console.error(e); }
      this.loading = false;
    },
    async loadDepartments() {
      try {
        var r = await executeJson({ function: "getQueueDepartments", branch_id: this.branchId }, hms);
        if (r.data && r.data.success) this.departments = (r.data.data && r.data.data.Departments) || [];
      } catch(e) { console.error(e); }
    },
    async loadCounters() {
      try {
        var r = await executeJson({ function: "getCounters", branch_id: this.branchId }, hms);
        if (r.data && r.data.success) this.counters = (r.data.data && r.data.data.Counters) || [];
      } catch(e) { console.error(e); }
    },
    async loadWaiting() {
      try {
        var r = await executeJson({ function: "getWaitingTickets", branch_id: this.branchId }, hms);
        if (r.data && r.data.success) this.waiting = (r.data.data && r.data.data.Tickets) || [];
      } catch(e) { console.error(e); }
    },
    async loadToday() {
      try {
        var r = await executeJson({ function: "getTodayTickets", branch_id: this.branchId, date: this.ticketsDate }, hms);
        if (r.data && r.data.success) this.todayTickets = (r.data.data && r.data.data.Tickets) || [];
      } catch(e) { console.error(e); }
    },
    async loadDisplayConfig() {
      try {
        var r = await executeJson({ function: "getDisplayToken", branch_id: this.branchId }, hms);
        if (r.data && r.data.success && r.data.data && r.data.data.display_token) {
          // We have a token — fetch full config
          var tk = r.data.data.display_token;
          this.displayConfig.display_token = tk;
          var c = await executeJson({ function: "getDisplayConfig", token: tk }, hms);
          if (c.data && c.data.success && c.data.data && c.data.data.Config) {
            var cfg = c.data.data.Config;
            this.displayConfig = {
              display_token:        cfg.display_token,
              display_name:         cfg.display_name || "Main Display",
              show_dept_ids:        cfg.show_dept_ids || "",
              now_serving_count:    cfg.now_serving_count || 3,
              next_up_count:        cfg.next_up_count || 5,
              theme:                cfg.theme || "light",
              show_logo:            cfg.show_logo == null ? 1 : Number(cfg.show_logo),
              show_clock:           cfg.show_clock == null ? 1 : Number(cfg.show_clock),
              announcement_voice:   cfg.announcement_voice || "en-US",
              announcement_volume:  cfg.announcement_volume == null ? 1.0 : Number(cfg.announcement_volume),
              scroll_message:       cfg.scroll_message || ""
            };
          }
        }
      } catch(e) { /* silent */ }
    },

    // ===== ISSUE TICKET =====
    openIssueTicket() {
      this.issueForm = this.emptyIssueForm();
      this.issueError = "";
      this.lastTicket = null;
      this.patientSearch = "";
      this.patientResults = [];
      this.walkInMode = false;
      this.showIssue = true;
    },
    onPatientSearch() {
      if (this.patientSearchTimer) { clearTimeout(this.patientSearchTimer); this.patientSearchTimer = null; }
      if (!this.patientSearch || this.patientSearch.length < 2) { this.patientResults = []; return; }
      var self = this;
      this.patientSearchTimer = setTimeout(async function() {
        try {
          var r = await executeJson({ function: "searchPatients", q: self.patientSearch.trim() }, hms);
          if (r.data && r.data.success) self.patientResults = (r.data.data && r.data.data.Patients) || [];
        } catch(e) {}
        self.patientSearchTimer = null;
      }, 300);
    },
    pickPatient(p) {
      this.issueForm.patient_id   = p.patient_id;
      this.issueForm.patient_no   = p.patient_no || '';
      this.issueForm.patient_name = (p.first_name + ' ' + (p.other_names ? p.other_names + ' ' : '') + p.last_name).trim();
      this.issueForm.phone        = p.phone || "";
      this.patientResults = []; this.patientSearch = "";
      this.walkInMode = false;
    },
    clearPatient() {
      this.issueForm.patient_id   = null;
      this.issueForm.patient_no   = "";
      this.issueForm.patient_name = "";
      this.issueForm.phone        = "";
      this.patientSearch          = "";
      this.patientResults         = [];
      this.walkInMode             = false;
    },
    enterWalkInMode() {
      this.walkInMode = true;
      this.patientResults = [];
      this.issueForm.patient_id = null;
      this.issueForm.patient_no = "";
      // Carry over what they typed in the search box as the walk-in name
      if (!this.issueForm.patient_name && this.patientSearch) {
        this.issueForm.patient_name = this.patientSearch.trim();
      }
    },
    exitWalkInMode() {
      this.walkInMode = false;
      this.issueForm.patient_name = "";
      this.issueForm.phone = "";
    },
    ageFromDob(dob) {
      if (!dob || dob === '0000-00-00') return '—';
      var d = new Date(dob);
      if (isNaN(d.getTime())) return '—';
      var t = new Date();
      var a = t.getFullYear() - d.getFullYear();
      var m = t.getMonth() - d.getMonth();
      if (m < 0 || (m === 0 && t.getDate() < d.getDate())) a--;
      return a;
    },
    async doIssueTicket() {
      this.issueError = "";
      if (!this.issueForm.queue_dept_id) { this.issueError = "Pick a department."; return; }
      if (!this.issueForm.patient_id && !this.issueForm.patient_name) {
        this.issueError = "Pick a registered patient or enter a walk-in name.";
        return;
      }
      this.processing = true;
      try {
        var r = await executeJson(Object.assign({ function: "issueTicket" }, this.issueForm), hms);
        if (r.data && r.data.success) {
          this.lastTicket = r.data.data;
          this.showToast("Ticket " + r.data.data.ticket_number + " issued");
          this.issueForm = this.emptyIssueForm();
          this.patientSearch = "";
          this.patientResults = [];
          this.walkInMode = false;
          await this.loadWaiting();
        } else { this.issueError = r.data.message || "Failed."; }
      } catch(e) { this.issueError = "Connection error."; }
      this.processing = false;
    },

    // ===== TICKET ACTIONS =====
    async quickCallNext(t) {
      // Find an open counter for this dept
      var counter = this.counters.find(c => c.queue_dept_id == t.queue_dept_id && c.status === 'open');
      if (!counter) { this.showToast("Open a counter for this department first", true); return; }
      this.processing = true;
      try {
        var r = await executeJson({ function: "callNextTicket", queue_dept_id: t.queue_dept_id, counter_id: counter.counter_id, branch_id: this.branchId }, hms);
        if (r.data && r.data.success) {
          this.showToast("Called " + r.data.data.Ticket.ticket_number + " to " + r.data.data.counter_name);
          await this.loadWaiting();
        } else { this.showToast(r.data.message || "Failed", true); }
      } catch(e) { this.showToast("Connection error", true); }
      this.processing = false;
    },
    async callNextForCounter(c) {
      this.processing = true;
      try {
        var r = await executeJson({ function: "callNextTicket", queue_dept_id: c.queue_dept_id, counter_id: c.counter_id, branch_id: this.branchId }, hms);
        if (r.data && r.data.success) {
          this.showToast("Called " + r.data.data.Ticket.ticket_number);
          await this.loadWaiting();
        } else { this.showToast(r.data.message || "No tickets", true); }
      } catch(e) { this.showToast("Connection error", true); }
      this.processing = false;
    },
    async recall(t) {
      this.processing = true;
      try {
        var r = await executeJson({ function: "recallTicket", ticket_id: t.ticket_id, counter_id: t.counter_id || "" }, hms);
        if (r.data && r.data.success) { this.showToast("Recalled"); await this.loadWaiting(); }
        else { this.showToast(r.data.message || "Failed", true); }
      } catch(e) { this.showToast("Connection error", true); }
      this.processing = false;
    },
    async serve(t) {
      this.processing = true;
      try {
        var r = await executeJson({ function: "serveTicket", ticket_id: t.ticket_id }, hms);
        if (r.data && r.data.success) { this.showToast("Marked as served"); await this.loadWaiting(); }
        else { this.showToast(r.data.message || "Failed", true); }
      } catch(e) { this.showToast("Connection error", true); }
      this.processing = false;
    },
    async cancel(t) {
      if (!confirm("Cancel ticket " + t.ticket_number + "?")) return;
      this.processing = true;
      try {
        var r = await executeJson({ function: "cancelTicket", ticket_id: t.ticket_id }, hms);
        if (r.data && r.data.success) { this.showToast("Cancelled"); await this.loadWaiting(); }
      } catch(e) { this.showToast("Connection error", true); }
      this.processing = false;
    },

    // ===== TRANSFER =====
    openTransfer(t) {
      this.transferTicket = t;
      this.transferToDept = "";
      this.showTransfer = true;
    },
    async doTransfer() {
      if (!this.transferTicket || !this.transferToDept) return;
      this.processing = true;
      try {
        var r = await executeJson({ function: "transferTicket", ticket_id: this.transferTicket.ticket_id, to_queue_dept_id: this.transferToDept }, hms);
        if (r.data && r.data.success) {
          this.showToast("Transferred → " + r.data.data.new_ticket_number);
          this.showTransfer = false;
          await this.loadWaiting();
        } else { this.showToast(r.data.message || "Failed", true); }
      } catch(e) { this.showToast("Connection error", true); }
      this.processing = false;
    },

    // ===== DEPARTMENTS =====
    openAddDept() { this.deptForm = this.emptyDeptForm(); this.editingDept = false; this.deptError = ""; this.showDeptForm = true; },
    openEditDept(d) {
      this.deptForm = Object.assign(this.emptyDeptForm(), {
        queue_dept_id: d.queue_dept_id, dept_name: d.dept_name, dept_code: d.dept_code || "",
        ticket_prefix: d.ticket_prefix, color: d.color || "#1a7fe8", icon: d.icon || "mdi mdi-account-clock",
        avg_serve_mins: Number(d.avg_serve_mins) || 10, display_order: Number(d.display_order) || 0,
        is_active: Number(d.is_active) || 0
      });
      this.editingDept = true; this.deptError = ""; this.showDeptForm = true;
    },
    async saveDept() {
      this.deptError = "";
      if (!this.deptForm.dept_name || !this.deptForm.ticket_prefix) { this.deptError = "Name and ticket prefix are required."; return; }
      this.processing = true;
      try {
        var fn = this.editingDept ? "updateQueueDepartment" : "createQueueDepartment";
        var r = await executeJson(Object.assign({ function: fn }, this.deptForm), hms);
        if (r.data && r.data.success) {
          this.showToast(this.editingDept ? "Department updated" : "Department created");
          this.showDeptForm = false;
          await this.loadDepartments();
        } else { this.deptError = r.data.message || "Failed."; }
      } catch(e) { this.deptError = "Connection error."; }
      this.processing = false;
    },

    // ===== COUNTERS =====
    openAddCounter() { this.counterForm = { queue_dept_id: "", counter_name: "", counter_code: "" }; this.counterError = ""; this.showCounterForm = true; },
    async saveCounter() {
      this.counterError = "";
      if (!this.counterForm.queue_dept_id || !this.counterForm.counter_name) { this.counterError = "Department and counter name required."; return; }
      this.processing = true;
      try {
        var r = await executeJson(Object.assign({ function: "createCounter" }, this.counterForm), hms);
        if (r.data && r.data.success) { this.showToast("Counter created"); this.showCounterForm = false; await this.loadCounters(); }
        else { this.counterError = r.data.message || "Failed."; }
      } catch(e) { this.counterError = "Connection error."; }
      this.processing = false;
    },
    async openCounter(c) {
      this.processing = true;
      try {
        var r = await executeJson({ function: "openCounter", counter_id: c.counter_id }, hms);
        if (r.data && r.data.success) { this.showToast("Counter opened"); await this.loadCounters(); }
      } catch(e) { this.showToast("Connection error", true); }
      this.processing = false;
    },
    async pauseCounter(c) {
      this.processing = true;
      try {
        var r = await executeJson({ function: "pauseCounter", counter_id: c.counter_id }, hms);
        if (r.data && r.data.success) { this.showToast("Counter paused"); await this.loadCounters(); }
      } catch(e) { this.showToast("Connection error", true); }
      this.processing = false;
    },
    async closeCounter(c) {
      this.processing = true;
      try {
        var r = await executeJson({ function: "closeCounter", counter_id: c.counter_id }, hms);
        if (r.data && r.data.success) { this.showToast("Counter closed"); await this.loadCounters(); }
      } catch(e) { this.showToast("Connection error", true); }
      this.processing = false;
    },

    // ===== DISPLAY CONFIG =====
    async saveDisplayConfig() {
      this.displayError = "";
      this.processing = true;
      try {
        var r = await executeJson(Object.assign({ function: "saveDisplayConfig" }, this.displayConfig), hms);
        if (r.data && r.data.success) {
          this.showToast("Display config saved");
          if (r.data.data && r.data.data.display_token) this.displayConfig.display_token = r.data.data.display_token;
        } else { this.displayError = r.data.message || "Failed."; }
      } catch(e) { this.displayError = "Connection error."; }
      this.processing = false;
    },
    copyDisplayUrl() {
      navigator.clipboard.writeText(this.displayUrl).then(() => this.showToast("URL copied"));
    }
  }
};
</script>
